import React from "react";
import { createPortal } from "react-dom";
import { MessageCircle, Phone, Share2, X } from "lucide-react";
import { formatDateString } from "../../utils";
import { ICONS } from "../../utils/icons";
import {
  ATTENDANCE_DETAIL_INFO_LABEL_CLASS,
  ATTENDANCE_DETAIL_INFO_ROW_CLASS,
  ATTENDANCE_DETAIL_MODAL_CLOSE_BUTTON_CLASS,
  ATTENDANCE_DETAIL_MODAL_CONTAINER_CLASS,
  ATTENDANCE_DETAIL_MODAL_CONTENT_CLASS,
  ATTENDANCE_DETAIL_MODAL_INNER_CLASS,
  ATTENDANCE_DETAIL_MODAL_OVERLAY_CLASS,
  ATTENDANCE_DETAIL_SECTION_TITLE_CLASS,
} from "./attendanceMobileStyles";

export default function AttendanceDetailModal({
  onClose,
  onShare,
  targetGroup,
  theme,
  viewingStudent,
}) {
  if (!viewingStudent) return null;

  return createPortal(
    <div className={ATTENDANCE_DETAIL_MODAL_OVERLAY_CLASS} style={{ zIndex: 1000, backfaceVisibility: "hidden" }} onClick={onClose}>
      <div className={ATTENDANCE_DETAIL_MODAL_CONTAINER_CLASS} onClick={(e) => e.stopPropagation()}>
        <div className="absolute top-5 right-5 z-20">
          <button onClick={onClose} className={ATTENDANCE_DETAIL_MODAL_CLOSE_BUTTON_CLASS}><X size={20} /></button>
        </div>

        <div className={ATTENDANCE_DETAIL_MODAL_CONTENT_CLASS}>
          <div className={ATTENDANCE_DETAIL_MODAL_INNER_CLASS}>
            <div className="flex flex-col items-center text-center mb-8 bg-gradient-to-b from-green-50/80 to-white -mx-8 -mt-12 pt-16 pb-8 px-8">
              <div className="relative mb-4">
                <div className="w-28 h-28 rounded-[2.5rem] overflow-hidden shadow-lg border-4 border-white bg-white">
                  {viewingStudent.photoUrl ? (
                    <img src={viewingStudent.photoUrl} alt={viewingStudent.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full p-2 bg-white flex items-center justify-center">
                      <img src={targetGroup === "teachers" ? (viewingStudent.gender === "여" ? ICONS.teacher_female : ICONS.teacher_male) : (viewingStudent.gender === "남" ? "/images/boy_3d_light.png" : "/images/girl_3d_light.png")} alt="Profile" className="w-full h-full object-contain" />
                    </div>
                  )}
                </div>
                {viewingStudent.faithState === "초신자" && (
                  <div className="absolute -bottom-2 -right-2 bg-yellow-400 text-white text-[10px] font-black px-3 py-1.5 rounded-full shadow-sm border-[3px] border-white animate-pulse">
                    NEW
                  </div>
                )}
              </div>
              <h2 className="text-3xl font-extrabold text-gray-900 mb-1">{viewingStudent.name}</h2>
              <div className="flex flex-col items-center gap-2">
                {viewingStudent.grade ? (
                  <div className="flex items-center justify-center gap-1.5">
                    <span className="bg-blue-50 text-blue-600 px-3 py-1 rounded-full text-xs font-bold border border-blue-100">{viewingStudent.grade}</span>
                    {viewingStudent.class && <span className="bg-gray-50 text-gray-500 px-3 py-1 rounded-full text-xs font-bold border border-gray-100">{viewingStudent.class}</span>}
                  </div>
                ) : (
                  <p className="text-gray-500 font-medium text-lg">{viewingStudent.role} {viewingStudent.charge && `• ${viewingStudent.charge}`}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 w-full mb-10">
              <a href={`tel:${viewingStudent.phone}`} className={`py-4 rounded-3xl ${theme.bg} text-white font-bold text-lg flex items-center justify-center gap-2 shadow-lg ${theme.shadow} hover:opacity-90 hover:scale-[0.98] active:scale-[0.95] transition-all duration-200`}>
                <Phone size={20} fill="currentColor" /> 전화
              </a>
              <a href={`sms:${viewingStudent.phone}`} className="py-4 rounded-3xl bg-[#FFECA1] text-[#4A3B10] font-bold text-lg flex items-center justify-center gap-2 shadow-lg shadow-yellow-400/20 hover:bg-[#FFD54F] hover:scale-[0.98] active:scale-[0.95] transition-all duration-200">
                <MessageCircle size={20} /> 문자
              </a>
            </div>

            <div className="w-full space-y-1 mb-8">
              <h3 className={ATTENDANCE_DETAIL_SECTION_TITLE_CLASS}>기본 정보</h3>

              <div className={ATTENDANCE_DETAIL_INFO_ROW_CLASS}>
                <span className={ATTENDANCE_DETAIL_INFO_LABEL_CLASS}>생년월일</span>
                <span className="text-gray-900 font-bold text-xl">{viewingStudent.birth ? formatDateString(viewingStudent.birth) : "-"}</span>
              </div>
              <div className={ATTENDANCE_DETAIL_INFO_ROW_CLASS}>
                <span className={ATTENDANCE_DETAIL_INFO_LABEL_CLASS}>학교/직장</span>
                <span className="text-gray-900 font-bold text-xl">{viewingStudent.school || "-"}</span>
              </div>
              <div className={ATTENDANCE_DETAIL_INFO_ROW_CLASS}>
                <span className={ATTENDANCE_DETAIL_INFO_LABEL_CLASS}>주소</span>
                <span className="text-gray-900 font-bold text-lg text-right break-keep w-2/3">{viewingStudent.address || "-"}</span>
              </div>
            </div>

            <div className="w-full space-y-1 mb-8">
              <h3 className={ATTENDANCE_DETAIL_SECTION_TITLE_CLASS}>활동 기록</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 p-5 rounded-3xl border border-gray-100 flex flex-col justify-between h-32 relative overflow-hidden group">
                  <div className="absolute right-0 top-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                    <img src={ICONS.warning} width={48} alt="" />
                  </div>
                  <span className="text-gray-500 font-bold text-sm z-10">최근 심방</span>
                  <span className="text-gray-900 font-black text-2xl z-10">{viewingStudent.lastVisit && viewingStudent.lastVisit !== "-" ? formatDateString(viewingStudent.lastVisit).slice(2) : "-"}</span>
                </div>
                <div className="bg-green-50 p-5 rounded-3xl border border-green-100 flex flex-col justify-between h-32 relative overflow-hidden group">
                  <div className="absolute right-0 top-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                    <img src={ICONS.attendance} width={48} alt="" />
                  </div>
                  <span className="text-green-700 font-bold text-sm z-10">최근 출석</span>
                  <span className="text-green-900 font-black text-2xl z-10">{viewingStudent.lastAttendance && viewingStudent.lastAttendance !== "-" ? formatDateString(viewingStudent.lastAttendance).slice(2) : "-"}</span>
                </div>
              </div>
            </div>

            <div className="w-full mb-8">
              <h3 className={ATTENDANCE_DETAIL_SECTION_TITLE_CLASS}>메모</h3>
              <div className="bg-[#FFF9E5] p-6 rounded-3xl text-gray-800 font-medium leading-relaxed shadow-sm">
                {viewingStudent.notes || "특이사항이 없습니다."}
              </div>
            </div>

            <div className="flex justify-center gap-6 mt-4 pt-6 border-t border-gray-100">
              <button onClick={onShare} className="flex flex-col items-center gap-1 text-gray-400 hover:text-gray-800 transition-colors group">
                <div className="p-3 rounded-full bg-gray-50 group-hover:bg-gray-100 transition-colors">
                  <Share2 size={20} />
                </div>
                <span className="text-xs font-medium">공유</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
